import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaCheck, FaWhatsapp } from 'react-icons/fa';
import { zodResolver } from '@hookform/resolvers/zod';
import { createLoginSchema, LoginFormValues } from '@/utils/validations/login.schema';
import InputField from '@/components/common/InputField';
import CaptchaBox from '@/components/common/CaptchaBox';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { BsFillPersonFill } from 'react-icons/bs';
import { BiSolidLock } from 'react-icons/bi';
import { MdShield } from 'react-icons/md';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks';
import { setToken } from '@/store/slices/authSlice';
import { ApiError } from '@/types/error';
import { getPlatform } from '@/utils';
import { Loader } from '@/components/common/Loader';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaRefresh, setCaptchaRefresh] = useState(0);

  const [loginMutation, { isLoading, error: loginApiError }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(createLoginSchema()),
    defaultValues: {
      username: '',
      password: '',
      captchaText: '',
      rememberMe: true,
    },
    mode: 'onChange',
  });
  const checked = watch('rememberMe');

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const payload = {
        username: data.username,
        password: data.password,
        device: getPlatform(),
        captchaId,
        captchaText: data.captchaText,
      };

      const res = await loginMutation(payload).unwrap();

      if (res?.accessToken) {
        localStorage.setItem('showWelcomeBanner', 'true');
        dispatch(setToken(res.accessToken));
        // if (
        //   res.passwordChanged !== null &&
        //   res.passwordChanged !== undefined &&
        //   res.passwordChanged === false
        // ) {
        //   dispatch(openChangePasswordModal());
        //   return;
        // }
        navigate('/', { replace: true });
      }
    } catch (error) {
      // const message = (error as ApiError)?.data?.message || 'Invalid username or password';
      // toast.error(message);
      setValue('captchaText', '');
      setCaptchaRefresh((prev) => prev + 1);
    }
  };
  const errorMessage =
    errors.username?.message ||
    errors.password?.message ||
    errors?.captchaText?.message ||
    (loginApiError as ApiError | undefined)?.data?.message;

  return (
    <div
      className="flex justify-between flex-col relative h-full !bg-no-repeat !bg-cover !bg-center"
      style={{ background: `url('/images/login-bg.webp')` }}
    >
      {isLoading && (
        <Loader className={'h-[100dvh] flex items-center justify-center bg-black/50'} />
      )}
      {/* Back Button */}
      <Link
        to={'/'}
        className="ml-2.5 mt-2.5 inline-flex size-9 items-center justify-center rounded-full bg-white"
      >
        <i className={'icon-arrow-down text-lg rotate-90 text-black'} />
      </Link>

      {/* Form Card */}
      <div className="flex flex-col">
        {/* Banner */}
        <div className="w-[96%] mx-auto">
          <img src="/images/login-banner.webp" alt="banner" className="w-full" />
        </div>

        <div className="py-7 bg-white rounded-t-[1rem]">
          <form onSubmit={handleSubmit(onSubmit)} className={'w-5/6 mx-auto'}>
            <h1 className="mb-6 text-center text-[1.875rem] font-normal">LOGIN</h1>

            <div className="space-y-5">
              <InputField
                control={control}
                name="username"
                label="Username"
                startIcon={<BsFillPersonFill className={'text-[1.8rem]'} />}
              />

              <InputField
                control={control}
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                startIcon={<BiSolidLock className={'text-[1.8rem]'} />}
                endIcon={
                  <button
                    className="text-[1.625rem]"
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <IoMdEye /> : <IoMdEyeOff />}
                  </button>
                }
              />

              <InputField
                control={control}
                name="captchaText"
                label="Validation Code"
                startIcon={<MdShield className={'text-[1.8rem]'} />}
                endIcon={
                  <CaptchaBox
                    onCaptchaChange={setCaptchaId}
                    refreshTrigger={captchaRefresh || undefined}
                  />
                }
                endIconClassName={'!right-0'}
              />

              <label className="flex cursor-pointer items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setValue('rememberMe', !!e.target.checked)}
                  className="hidden"
                />

                <span
                  className={`flex size-[1.125rem] items-center justify-center rounded border transition-all bg-primary border-black/70`}
                >
                  {checked && <FaCheck size={10} className="text-black" />}
                </span>

                <span className="text-[1.094rem] text-black">Remember me</span>
              </label>

              {errorMessage && <p className="text-red-600 text-[0.938rem]">{errorMessage}</p>}

              <button
                type="submit"
                disabled={isLoading || !captchaId}
                className="bg-primary p-3 rounded flex justify-center items-center disabled:bg-black-300 disabled:text-black-600 disabled:cursor-not-allowed w-52 h-[3.125rem] mx-auto text-[1.063rem] font-extrabold"
              >
                Login
              </button>

              <div className="pt-3">
                <div className="mb-4 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-300" />
                  <span className="italic text-lg tracking-widest font-ubuntu text-gray-700">
                    Contact details
                  </span>
                  <div className="h-px flex-1 bg-gray-300" />
                </div>

                <div className="flex justify-center text-white">
                  <a
                    href="#"
                    className="px-1.5 bg-secondary rounded flex justify-center items-center gap-2 h-[1.875rem] text-[0.688rem] underline font-bold self-center mr-2"
                  >
                    <FaWhatsapp size={32} />
                    Admin Number
                  </a>

                  <a
                    href="#"
                    className="px-1.5 bg-secondary rounded flex justify-center items-center gap-2 h-[1.875rem] text-[0.688rem] underline font-bold self-center"
                  >
                    <FaWhatsapp size={32} />
                    Customer Care number
                  </a>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
